import { useState } from "react";
import { useTranslation } from "react-i18next";
import BrokerSelecter from "~/components/mqtt/broker-select";
import { Input, Label } from "~/components/ui";
import type {
  ActionConfig,
  ActionEmailConfig,
  ActionEventType,
  ActionMqttConfig,
  MqttBroker,
} from "~/types/api";
import { actionEventTypeToString } from "~/utils/action-util";

export default function ActionConfigSelector({
  value,
  onChange,
  options,
  email = false,
  mqtt = false,
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    label: string;
    value: MqttBroker[] | number;
  }[];
  email?: boolean;
  mqtt?: boolean;
}) {
  const { t } = useTranslation();

  const [config, setConfig] = useState<ActionConfig>(() => {
    try {
      return value ? JSON.parse(value) : {};
    } catch {
      return {};
    }
  });
  const brokers = options?.find((x) => x.label == "brokers")
    ?.value as MqttBroker[];
  const eventType = options?.find((x) => x.label == "eventType")
    ?.value as ActionEventType;

  function handleMqttConfigChange(mqttConfig: ActionMqttConfig) {
    setConfig({ ...config, mqtt: mqttConfig });
    onChange(JSON.stringify({ ...config, mqtt: mqttConfig }));
  }

  function handleEmailConfigChange(emailTemplate: ActionEmailConfig) {
    setConfig({ ...config, email: emailTemplate });
    onChange(JSON.stringify({ ...config, email: emailTemplate }));
  }

  return (
    <>
      {(email || mqtt) && (
        <div className="flex ml-2">
          <Label className="text-sm text-gray-500">
            {t("actions.config.availableTitleVariable", {
              resource: actionEventTypeToString(eventType),
            })}
          </Label>
          {eventType != undefined && (
            <Label className="ml-1 text-sm text-gray-500">
              {t("actions.config.availableVariables." + eventType)}
            </Label>
          )}
        </div>
      )}
      {email && (
        <ActionConfigEmail
          emailTemplate={config.email ?? {}}
          onChange={(val) => handleEmailConfigChange(val)}
        />
      )}
      <div></div>
      {mqtt && (
        <ActionConfigMqtt
          config={config.mqtt ?? {}}
          onChange={(val) => handleMqttConfigChange(val)}
          options={{
            eventType: eventType,
            brokers: brokers,
          }}
        />
      )}
    </>
  );
}

function ActionConfigEmail({
  emailTemplate,
  onChange,
}: {
  emailTemplate: ActionEmailConfig;
  onChange: (val: ActionEmailConfig) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="ml-2">
      <Label className="text-sm">{t("actions.config.emailTitle")}</Label>
      <Input
        value={emailTemplate?.emailSubjectTemplate ?? ""}
        placeholder={t("actions.config.placeholders.email")}
        onChange={(e) =>
          onChange({
            emailSubjectTemplate: e.target.value,
          } as ActionEmailConfig)
        }
      />
      <Label className="text-sm text-gray-500">
        {t("actions.config.emailTemplateDescription")}
      </Label>
    </div>
  );
}

function ActionConfigMqtt({
  config,
  onChange,
  options,
}: {
  config: ActionMqttConfig;
  onChange: (val: ActionMqttConfig) => void;
  options?: { eventType: ActionEventType; brokers: MqttBroker[] };
}) {
  const { t } = useTranslation();
  return (
    <div className="ml-2">
      <Label className="text-sm">{t("actions.config.mqttTitle")}</Label>
      <BrokerSelecter
        value={config?.brokerId ?? ""}
        onChange={(val) => onChange({ ...config, brokerId: val as string })}
        options={options?.brokers ?? []}
        canCreate
      />
      <div className="mt-1"></div>
      <Input
        value={config?.topicTemplate ?? ""}
        placeholder={t("actions.config.placeholders.mqtt")}
        onChange={(e) => onChange({ ...config, topicTemplate: e.target.value })}
      />
      <Label className="text-sm text-gray-500">
        {t("actions.config.mqttTemplateDescription")}
      </Label>
    </div>
  );
}
